import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowRightLeft, XCircle, FileDown } from "lucide-react";
import { LocationSelect } from "@/components/LocationSelect";

export function TransferDialog({ open, onOpenChange, asset, onDone }) {
  const [form, setForm] = useState({
    new_location: asset.location || "",
    new_assigned_to: asset.assigned_to || "",
    effective_date: new Date().toISOString().slice(0, 10),
    reason: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    if (form.new_location === asset.location && form.new_assigned_to === (asset.assigned_to || "")) {
      toast.error("Nothing to change — location & assignee are the same"); return;
    }
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/transfer`, form);
      toast.success("Transfer recorded");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transfer failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Transfer / Relocate Asset</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="text-xs text-slate-500">Current</div>
            <div className="font-medium">Location: {asset.location || "—"} · Assignee: {asset.assigned_to || "—"}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>New Location *</Label><LocationSelect value={form.new_location} onChange={(v) => set("new_location", v)} testId="transfer-new-location" allowNone={false} /></div>
            <div><Label>New Assignee</Label><Input value={form.new_assigned_to} onChange={(e) => set("new_assigned_to", e.target.value)} data-testid="transfer-new-assignee" /></div>
            <div className="col-span-2"><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} /></div>
            <div className="col-span-2"><Label>Reason *</Label><Input value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Relocation to Plant B for Q2 project" data-testid="transfer-reason" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="confirm-transfer-btn">
            <ArrowRightLeft className="w-4 h-4 mr-2" /> {busy ? "Transferring..." : "Record Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REASONS = [
  { v: "obsolete", label: "Obsolete / End of Useful Life" },
  { v: "damaged", label: "Beyond Economic Repair (Damaged)" },
  { v: "sold", label: "Sold / Transferred to Third Party" },
  { v: "scrap", label: "Sent for Scrap" },
  { v: "lost", label: "Lost / Untraceable" },
  { v: "other", label: "Other" },
];
const METHODS = [
  { v: "scrap", label: "Sent to Scrap Vendor" },
  { v: "sale", label: "Sale (Third-Party Buyer)" },
  { v: "donation", label: "Donation" },
  { v: "destruction", label: "Physical Destruction" },
  { v: "other", label: "Other" },
];

export function WriteOffDialog({ open, onOpenChange, asset, onDone }) {
  const [form, setForm] = useState({
    reason: "obsolete",
    disposal_method: "scrap",
    disposal_value: "0",
    disposal_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/assets/${asset.id}/write-off`, {
        ...form,
        disposal_value: parseFloat(form.disposal_value) || 0,
      });
      toast.success(`Certificate ${data.cert_number} generated. Asset marked disposed.`);
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Write-off failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700">Write-off / Dispose Asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-800 text-xs">
            This will mark <b>{asset.asset_id} — {asset.name}</b> as disposed and generate a signed Write-off Certificate. This action cannot be undone.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Reason *</Label>
              <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
                <SelectTrigger data-testid="writeoff-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Method *</Label>
              <Select value={form.disposal_method} onValueChange={(v) => set("disposal_method", v)}>
                <SelectTrigger data-testid="writeoff-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Disposal Date *</Label><Input type="date" value={form.disposal_date} onChange={(e) => set("disposal_date", e.target.value)} /></div>
            <div><Label>Disposal Value Received (₹)</Label><Input type="number" min="0" step="0.01" value={form.disposal_value} onChange={(e) => set("disposal_value", e.target.value)} data-testid="writeoff-value" /></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Buyer name, scrap vendor ref, condition notes..." /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="confirm-writeoff-btn">
            <XCircle className="w-4 h-4 mr-2" /> {busy ? "Processing..." : "Confirm Write-off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
